import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserRole } from "../../../core/data/slice/authSlice";
import { useCenter } from "../../peoples/centers/hooks/useCenter";
import { all_routes } from "../../router/all_routes";
import CommonSelect, { Option } from "../../../core/common/commonSelect";
import Table from "../../../core/common/dataTable/index";
import { useOfflinePayments } from "../hooks/useOfflinePayments";
import { OfflinePayment } from "../api/offlinePaymentApi";
import AddOfflinePaymentModal from "../components/AddOfflinePaymentModal";
import EditOfflinePaymentModal from "../components/EditOfflinePaymentModal";

const statusBadge = (status: string) => {
  const map: Record<string, { cls: string; label: string }> = {
    approved: { cls: "success", label: "Approved" },
    rejected: { cls: "danger", label: "Rejected" },
    pending: { cls: "warning", label: "Pending" },
  };
  const s = map[status] || { cls: "secondary", label: status };
  return (
    <span className={`badge badge-soft-${s.cls} d-inline-flex align-items-center`}>
      <i className="ti ti-circle-filled fs-5 me-1" />
      {s.label}
    </span>
  );
};

const OfflinePayments = () => {
  const routes = all_routes;
  const userRole = useSelector(selectUserRole);
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  const {
    payments,
    loading,
    error,
    filters,
    paginationData,
    updateFilters,
    refetch,
    handleCreate,
    handleUpdate,
    isCreating,
    isUpdating,
  } = useOfflinePayments();

  const { useCenterList } = useCenter({ skip: true });
  const { data: centerResponse } = useCenterList();
  const centers = centerResponse?.data?.items || [];
  const centerOptions = centers.map((c) => ({
    value: c.id.toString(),
    label: c.name,
  }));

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];

  // UI state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editPayment, setEditPayment] = useState<OfflinePayment | null>(null);
  const [search, setSearch] = useState(filters.search || "");
  const [pageNumber, setPageNumber] = useState(filters.page || 1);
  const [recordsPerPage, setRecordsPerPage] = useState(filters.size || 10);

  // Temp filter state (for dropdown)
  const [tempFilters, setTempFilters] = useState({
    center_id: filters.center_id,
    status: filters.status,
    from_date: filters.from_date || "",
    to_date: filters.to_date || "",
  });

  const handleApplyClose = () => {
    dropdownMenuRef.current?.classList.remove("show");
  };

  const handleSearchChange = (text: string) => {
    setSearch(text);
    updateFilters({ search: text });
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    setPageNumber(page);
    if (pageSize) setRecordsPerPage(pageSize);
    updateFilters(pageSize ? { page, size: pageSize } : { page });
  };

  const findOption = (opts: Option[], val?: string) =>
    val ? opts.find((o) => o.value === val) || null : null;

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      render: (id: number) => <span className="fw-medium">#OP-{id}</span>,
    },
    {
      title: "Student ID",
      dataIndex: "student_id",
    },
    {
      title: "Center ID",
      dataIndex: "center_id",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (amount: string | number) =>
        `₹ ${parseFloat(String(amount) || "0").toFixed(2)}`,
    },
    {
      title: "Payment Date",
      dataIndex: "payment_date",
      render: (date: string | null) => date || <span className="text-muted">—</span>,
    },
    {
      title: "Method",
      dataIndex: "payment_method",
      render: (method: string) =>
        method ? (
          <span className="badge badge-soft-info text-capitalize">{method}</span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      title: "Reference No.",
      dataIndex: "reference_number",
      render: (ref: string) => ref || <span className="text-muted">—</span>,
    },
    {
      title: "Receipt",
      dataIndex: "receipt_path",
      render: (path: string) => {
        if (!path) return <span className="text-muted">—</span>;
        const isUrl = path.startsWith("http");
        const href = isUrl ? path : `#`;
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-xs btn-outline-primary d-inline-flex align-items-center gap-1"
            style={{ fontSize: 11 }}
          >
            <i className="ti ti-file-description" />
            View
          </a>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => statusBadge(status),
    },
    {
      title: "Action",
      render: (record: OfflinePayment) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <button
                  className="dropdown-item rounded-1"
                  onClick={() => setEditPayment(record)}
                >
                  <i className="ti ti-edit me-2" />
                  Edit Payment
                </button>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Offline Payments</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">Fee Collection</li>
                  <li className="breadcrumb-item active">Offline Payments</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <button
                  className="btn btn-primary d-flex align-items-center"
                  onClick={() => setShowAddModal(true)}
                >
                  <i className="ti ti-square-rounded-plus me-2" />
                  Record Payment
                </button>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="row g-3 mb-4">
            {[
              {
                label: "Total Payments",
                value: paginationData.totalItems,
                icon: "ti-cash",
                color: "#667eea",
                bg: "rgba(102,126,234,0.1)",
              },
              {
                label: "Approved",
                value: payments.filter((p) => p.status === "approved").length,
                icon: "ti-circle-check",
                color: "#38ef7d",
                bg: "rgba(56,239,125,0.1)",
              },
              {
                label: "Pending",
                value: payments.filter((p) => p.status === "pending").length,
                icon: "ti-clock",
                color: "#f7971e",
                bg: "rgba(247,151,30,0.1)",
              },
              {
                label: "Rejected",
                value: payments.filter((p) => p.status === "rejected").length,
                icon: "ti-circle-x",
                color: "#eb3349",
                bg: "rgba(235,51,73,0.1)",
              },
            ].map((stat, idx) => (
              <div className="col-6 col-xl-3" key={idx}>
                <div
                  className="card border-0 h-100"
                  style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}
                >
                  <div className="card-body d-flex align-items-center gap-3 py-3">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 48,
                        height: 48,
                        background: stat.bg,
                      }}
                    >
                      <i
                        className={`ti ${stat.icon} fs-22`}
                        style={{ color: stat.color }}
                      />
                    </div>
                    <div>
                      <div className="fw-bold fs-22 lh-1">{stat.value}</div>
                      <small className="text-muted">{stat.label}</small>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Table Card */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Offline Payment Records</h4>
              <div className="d-flex align-items-center flex-wrap">
                {/* Filter Dropdown */}
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                    onClick={() =>
                      setTempFilters({
                        center_id: filters.center_id,
                        status: filters.status,
                        from_date: filters.from_date || "",
                        to_date: filters.to_date || "",
                      })
                    }
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom">
                        <div className="row g-3">
                          {userRole === "superadmin" && (
                            <div className="col-md-6">
                              <label className="form-label">Center</label>
                              <CommonSelect
                                className="select"
                                options={centerOptions}
                                onChange={(opt: Option | null) =>
                                  setTempFilters((p) => ({
                                    ...p,
                                    center_id: opt ? parseInt(opt.value) : undefined,
                                  }))
                                }
                                value={findOption(
                                  centerOptions,
                                  tempFilters.center_id?.toString()
                                )}
                              />
                            </div>
                          )}
                          <div className={userRole === "superadmin" ? "col-md-6" : "col-md-12"}>
                            <label className="form-label">Status</label>
                            <CommonSelect
                              className="select"
                              options={statusOptions}
                              onChange={(opt: Option | null) =>
                                setTempFilters((p) => ({
                                  ...p,
                                  status: opt?.value || undefined,
                                }))
                              }
                              value={findOption(statusOptions, tempFilters.status)}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">From Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={tempFilters.from_date}
                              onChange={(e) =>
                                setTempFilters((p) => ({
                                  ...p,
                                  from_date: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">To Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={tempFilters.to_date}
                              onChange={(e) =>
                                setTempFilters((p) => ({
                                  ...p,
                                  to_date: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link
                          to="#"
                          className="btn btn-light me-3"
                          onClick={() => {
                            const reset = {
                              center_id: undefined,
                              status: undefined,
                              from_date: "",
                              to_date: "",
                            };
                            setTempFilters(reset);
                            updateFilters({
                              center_id: undefined,
                              status: undefined,
                              from_date: undefined,
                              to_date: undefined,
                              search: "",
                              page: 1,
                            });
                            setSearch("");
                            handleApplyClose();
                          }}
                        >
                          Reset
                        </Link>
                        <Link
                          to="#"
                          className="btn btn-primary"
                          onClick={() => {
                            updateFilters({
                              center_id: tempFilters.center_id,
                              status: tempFilters.status,
                              from_date: tempFilters.from_date || undefined,
                              to_date: tempFilters.to_date || undefined,
                              page: 1,
                            });
                            handleApplyClose();
                          }}
                        >
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-body p-0 py-3">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                  <p className="text-muted mt-3 mb-0">Loading payments...</p>
                </div>
              ) : error ? (
                <div className="alert alert-danger m-3">{error}</div>
              ) : (
                <Table
                  dataSource={payments}
                  columns={columns}
                  Selection={false}
                  onSearchChange={handleSearchChange}
                  pagination={{
                    current: pageNumber,
                    pageSize: recordsPerPage,
                    total: paginationData.totalItems || 0,
                    onChange: handlePageChange,
                    pageSizeOptions: ["All", 10, 20, 30],
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <AddOfflinePaymentModal
          onSuccess={refetch}
          onClose={() => setShowAddModal(false)}
          handleCreate={handleCreate}
          isCreating={isCreating}
        />
      )}

      {/* Edit Modal */}
      {editPayment && (
        <EditOfflinePaymentModal
          payment={editPayment}
          onSuccess={refetch}
          onClose={() => setEditPayment(null)}
          handleUpdate={handleUpdate}
          isUpdating={isUpdating}
        />
      )}
    </>
  );
};

export default OfflinePayments;
